import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions } from '@cast/shared-types';
import {
  DboPerformanceRecommendationCategory,
  DboRecommendationImpact,
  DboPerformanceRecommendationStatus,
  DboRecommendationKind,
  DboRecommendationsResponse,
} from '@cast/types-database-optimizer';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const apiFn = apiClient.dbo.getPerformanceAdvisorRecommendations;

type FilterParams = {
  databaseId?: string;
  kinds?: DboRecommendationKind[];
  categories?: DboPerformanceRecommendationCategory[];
  impacts?: DboRecommendationImpact[];
  statuses?: DboPerformanceRecommendationStatus[];
};

export type PerformanceAdvisorRecommendationsQueryData =
  DboRecommendationsResponse;

export type PerformanceAdvisorRecommendationsQueryArgs<SELECT> = QueryOptions<
  PerformanceAdvisorRecommendationsQueryData,
  SELECT
> &
  FilterParams;

const DBO_PERFORMANCE_ADVISOR_RECOMMENDATIONS_QUERY_KEY =
  'dbo-performance-advisor-recommendations';

export const getPerformanceAdvisorRecommendationsQueryKey = ({
  databaseId,
  kinds,
  categories,
  impacts,
  statuses,
}: FilterParams = {}) => {
  if (!databaseId) {
    return [DBO_PERFORMANCE_ADVISOR_RECOMMENDATIONS_QUERY_KEY] as const;
  }
  if (
    kinds === undefined &&
    categories === undefined &&
    impacts === undefined &&
    statuses === undefined
  ) {
    return [
      DBO_PERFORMANCE_ADVISOR_RECOMMENDATIONS_QUERY_KEY,
      databaseId,
    ] as const;
  }
  return [
    DBO_PERFORMANCE_ADVISOR_RECOMMENDATIONS_QUERY_KEY,
    databaseId,
    kinds,
    categories,
    impacts,
    statuses,
  ] as const;
};

const getPerformanceAdvisorRecommendationsQueryOptions = ({
  databaseId,
  organizationId,
  enabled,
  ...filter
}: FilterParams & {
  organizationId: string;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getPerformanceAdvisorRecommendationsQueryKey({
      databaseId,
      ...filter,
    }),
    queryFn: async () => {
      const { data } = await apiFn({
        organizationId,
        databaseId: databaseId!,
        pageLimit: '1000',
        ...filter,
      });
      data.items.forEach((item) => {
        // @ts-expect-error - TODO(VytautasM): this will start failing if BE updates the response and
        // `kind` is already on the recommendation object. If so, remove the entire assignment.
        item.kind = item.indexDetails
          ? DboRecommendationKind.INDEX
          : DboRecommendationKind.QUERY;
        return item;
      });
      return data as DboRecommendationsResponse;
    },
    enabled: makeIsEnabled(!!databaseId, enabled),
  });

export const usePerformanceAdvisorRecommendationsQuery = <
  SELECT = PerformanceAdvisorRecommendationsQueryData
>({
  databaseId,
  enabled,
  kinds,
  categories,
  impacts,
  statuses,
  select,
  refetchInterval,
}: PerformanceAdvisorRecommendationsQueryArgs<SELECT>) => {
  const { currentOrganization } = useOrganizations();

  return useQuery({
    ...getPerformanceAdvisorRecommendationsQueryOptions({
      databaseId,
      organizationId: currentOrganization!.id,
      kinds,
      categories,
      impacts,
      statuses,
      enabled,
    }),
    select,
    refetchInterval,
  });
};
